/**
 * Letter and notification templates, with the categories, types and merge
 * fields the template composer offers.
 */

export interface TourTemplateCategory {
  id: number;
  categoryName: string;
  name: string;
  description: string;
}

export const TOUR_TEMPLATE_CATEGORIES: readonly TourTemplateCategory[] =
  Object.freeze([
    {
      id: 11,
      categoryName: 'Award Letter',
      name: 'Award Letter',
      description: 'Letters issued when an award is granted.',
    },
    {
      id: 12,
      categoryName: 'Final Award Letter',
      name: 'Final Award Letter',
      description: 'Letters issued when an award vests.',
    },
    {
      id: 13,
      categoryName: 'Share Crediting',
      name: 'Share Crediting',
      description: 'Registrar and depository correspondence.',
    },
    {
      id: 14,
      categoryName: 'User Notification',
      name: 'User Notification',
      description: 'Account and access notifications.',
    },
  ]);

export interface TourTemplateType {
  id: number;
  typeName: string;
  name: string;
}

export const TOUR_TEMPLATE_TYPES: readonly TourTemplateType[] = Object.freeze([
  { id: 21, typeName: 'Email', name: 'Email' },
  { id: 22, typeName: 'Letter', name: 'Letter' },
  { id: 23, typeName: 'System Notification', name: 'System Notification' },
]);

/** Merge fields offered when composing a template in a given category. */
export interface TourTemplateField {
  id: number;
  fieldName: string;
  displayName: string;
  placeholder: string;
  dataType: string;
}

export const TOUR_TEMPLATE_FIELDS: readonly TourTemplateField[] = Object.freeze(
  [
    {
      id: 31,
      fieldName: 'employeeName',
      displayName: 'Employee Name',
      placeholder: '{{EmployeeName}}',
      dataType: 'String',
    },
    {
      id: 32,
      fieldName: 'employeeId',
      displayName: 'Employee ID',
      placeholder: '{{EmployeeId}}',
      dataType: 'String',
    },
    {
      id: 33,
      fieldName: 'awardNo',
      displayName: 'Award No',
      placeholder: '{{AwardNo}}',
      dataType: 'String',
    },
    {
      id: 34,
      fieldName: 'planName',
      displayName: 'Plan Name',
      placeholder: '{{PlanName}}',
      dataType: 'String',
    },
    {
      id: 35,
      fieldName: 'grantDate',
      displayName: 'Grant Date',
      placeholder: '{{GrantDate}}',
      dataType: 'Date',
    },
    {
      id: 36,
      fieldName: 'awardedShares',
      displayName: 'Awarded Shares',
      placeholder: '{{AwardedShares}}',
      dataType: 'Number',
    },
    {
      id: 37,
      fieldName: 'vestingDate',
      displayName: 'Vesting Date',
      placeholder: '{{VestingDate}}',
      dataType: 'Date',
    },
    {
      id: 38,
      fieldName: 'expiryDate',
      displayName: 'Response Due Date',
      placeholder: '{{ExpiryDate}}',
      dataType: 'Date',
    },
  ]
);

export interface TourTemplate {
  id: number;
  templateId: number;
  templateName: string;
  categoryId: number;
  categoryName: string;
  templateTypeId: number;
  typeName: string;
  subject: string;
  bodyContent: string;
  statusLookupId: number;
  status: string;
  statusName: string;
  isDraft: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  selectedFields: string[];
}

const letterBody = (heading: string, body: string): string =>
  `<p>Dear {{EmployeeName}},</p><h3>${heading}</h3><p>${body}</p>` +
  '<p>Yours sincerely,<br/>Northwind Share Plan Administration</p>';

export const TOUR_TEMPLATES: readonly TourTemplate[] = Object.freeze([
  {
    id: 4101,
    templateId: 4101,
    templateName: 'PSP Contingent Award Letter',
    categoryId: 11,
    categoryName: 'Award Letter',
    templateTypeId: 22,
    typeName: 'Letter',
    subject: 'Your {{PlanName}} award',
    bodyContent: letterBody(
      'Contingent Award',
      'We are pleased to confirm a contingent award of {{AwardedShares}} shares ' +
        'under {{PlanName}}, granted on {{GrantDate}} with reference {{AwardNo}}. ' +
        'Please respond by {{ExpiryDate}}.'
    ),
    statusLookupId: 1,
    status: 'Active',
    statusName: 'Active',
    isDraft: false,
    createdBy: 'Sarah Lim',
    createdAt: '2024-11-04T10:00:00Z',
    updatedBy: 'Arun Kumar',
    updatedAt: '2025-01-22T15:30:00Z',
    selectedFields: ['{{EmployeeName}}', '{{PlanName}}', '{{AwardedShares}}'],
  },
  {
    id: 4102,
    templateId: 4102,
    templateName: 'Final Award Vesting Letter',
    categoryId: 12,
    categoryName: 'Final Award Letter',
    templateTypeId: 22,
    typeName: 'Letter',
    subject: 'Vesting of your {{PlanName}} award',
    bodyContent: letterBody(
      'Final Award',
      'Award {{AwardNo}} vested on {{VestingDate}}. The shares will be credited ' +
        'to your securities account shortly.'
    ),
    statusLookupId: 1,
    status: 'Active',
    statusName: 'Active',
    isDraft: false,
    createdBy: 'Sarah Lim',
    createdAt: '2024-11-04T10:12:00Z',
    updatedBy: null,
    updatedAt: null,
    selectedFields: ['{{EmployeeName}}', '{{AwardNo}}', '{{VestingDate}}'],
  },
  {
    id: 4103,
    templateId: 4103,
    templateName: 'Share Registrar Instruction',
    categoryId: 13,
    categoryName: 'Share Crediting',
    templateTypeId: 22,
    typeName: 'Letter',
    subject: 'Allotment instruction - batch {{AwardNo}}',
    bodyContent: letterBody(
      'Allotment Instruction',
      'Please arrange for the allotment and crediting of the shares listed in ' +
        'the attached schedule.'
    ),
    statusLookupId: 1,
    status: 'Active',
    statusName: 'Active',
    isDraft: false,
    createdBy: 'Mei Ling Chua',
    createdAt: '2025-02-10T08:45:00Z',
    updatedBy: null,
    updatedAt: null,
    selectedFields: [],
  },
  {
    id: 4104,
    templateId: 4104,
    templateName: 'New User Welcome Email',
    categoryId: 14,
    categoryName: 'User Notification',
    templateTypeId: 21,
    typeName: 'Email',
    subject: 'Welcome to the Northwind share plan portal',
    bodyContent: letterBody(
      'Welcome',
      'Your account has been created. Use the link below to set your password ' +
        'and review your holdings.'
    ),
    statusLookupId: 1,
    status: 'Active',
    statusName: 'Active',
    isDraft: false,
    createdBy: 'Priya Sharma',
    createdAt: '2025-03-01T09:00:00Z',
    updatedBy: null,
    updatedAt: null,
    selectedFields: ['{{EmployeeName}}'],
  },
  {
    id: 4105,
    templateId: 4105,
    templateName: 'RSP Award Letter (Draft)',
    categoryId: 11,
    categoryName: 'Award Letter',
    templateTypeId: 22,
    typeName: 'Letter',
    subject: 'Your {{PlanName}} award',
    bodyContent: letterBody('Contingent Award', 'Draft copy pending review.'),
    statusLookupId: 2,
    status: 'Draft',
    statusName: 'Draft',
    isDraft: true,
    createdBy: 'Daniel Tan',
    createdAt: '2025-06-18T11:20:00Z',
    updatedBy: null,
    updatedAt: null,
    selectedFields: [],
  },
]);

export const TOUR_PRIMARY_TEMPLATE = TOUR_TEMPLATES[0];
